import { useV2ListProjectAttachmentsForAccountQuery } from "@generated";
import type { ProjectAttachment } from "../types";

export interface UseProjectAttachmentsListArgs {
  projectId: string;
  active: boolean;
}

export interface ProjectAttachmentsListPageMeta {
  recordCount: number;
  pageCount: number;
}

export const useProjectAttachmentsList = ({
  projectId,
  active,
}: UseProjectAttachmentsListArgs) => {
  const { data, error, isLoading, isFetching, refetch } =
    useV2ListProjectAttachmentsForAccountQuery(
      { filter: { project: projectId, active }, include: ["attachment-type"] },
      { refetchOnMountOrArgChange: true }
    );

  const included = (data as any)?.included ?? [];
  const typeNameById = new Map<string, string>();
  included.forEach((inc: any) => {
    if (inc?.type === "attachment-types") {
      typeNameById.set(String(inc.id), inc?.attributes?.name ?? "");
    }
  });

  const rows: ProjectAttachment[] = ((data?.data as any[]) ?? []).map((a) => {
    const typeId = a?.relationships?.["attachment-type"]?.data?.id;
    const typeIdStr = typeId != null ? String(typeId) : null;
    return {
      id: String(a.id),
      document: a?.attributes?.document ?? "",
      // Default to the requested filter value (not always true) so an archived
      // record with a missing `active` attribute isn't mislabeled as active.
      active: a?.attributes?.active ?? active,
      attachmentTypeId: typeIdStr,
      attachmentTypeName: typeIdStr ? typeNameById.get(typeIdStr) ?? "" : "",
    };
  });

  const pageMeta: ProjectAttachmentsListPageMeta = {
    recordCount: data?.meta?.["record-count"] ?? 0,
    pageCount: data?.meta?.["page-count"] ?? 0,
  };

  return { rows, pageMeta, error, isLoading, isFetching, refetch };
};

export default useProjectAttachmentsList;
